import { useContext, useRef, useState } from "react";
import { message } from "antd";
import axios from "axios";
import { AppContext } from "../../../store";
import { getAuthToken, handleLogin } from "../../../utils/common";
import { getSelectedSTObj } from "../../../utils/addressUtils";
import { cartAddUrl } from "../../../utils/constant";
import { addToCartErrorHandle } from "../../../utils/activityHelper";
import { addToCartDataPush } from "../../../utils/analyticsUtils";
import { logOperation } from "../../../utils/operationLogUtils";
import { CONTACT_US } from "./types";
import type { AddOnPurchaseCardProps } from "./types";

// 两端顺手买卡片复用现有加购协议，并在提交前执行接口返回的首购限购数。
export function useAddOnPurchaseCart(props: AddOnPurchaseCardProps, platform: "pc" | "mobile") {
  const { checkedCartItems, setCheckedCartItems, setCartCount } = useContext(AppContext);
  const [processing, setProcessing] = useState(false);
  const pending = useRef(false);
  const addToCart = async (quantity: number) => {
    const { product, activityId, loginState, previewFlag, onAddToCart } = props;
    if (loginState.status !== "login") {
      handleLogin();
      return;
    }
    if (pending.current || !product.available || quantity < 1) return;
    if (product.limitQty != null && quantity > product.limitQty) {
      message.error(`购买数量不能超过限购数量 ${product.limitQty}`);
      return;
    }
    if (previewFlag) {
      message.error("活动预热期间，该产品暂时无法加入购物车");
      return;
    }
    if (product.priceStatus === CONTACT_US) {
      message.error("该产品不支持网络订购，如有需要，请联系我们");
      return;
    }
    pending.current = true;
    setProcessing(true);
    const selectedST = getSelectedSTObj(loginState);
    logOperation({ opUser: loginState.user?.user?.userId, opTarget: "Add_To_Cart",
      opTargetId: product.sku, opContent: "Activity_Detail" });
    try {
      const resp = await axios(cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: getAuthToken() },
        data: { activityId, type: "activity", category: product.category, sku: product.sku,
          quantity, customerStNumber: selectedST?.accountNumber },
      });
      const result = resp.data;
      if (result?.status !== "success") {
        addToCartErrorHandle(result, message, "common", platform);
        return;
      }
      const ids: number[] = result.data?.cartIds || [];
      const updatedItems = new Set(checkedCartItems);
      if (ids.length) updatedItems.add(ids[0]);
      setCheckedCartItems(updatedItems);
      setCartCount(result.data?.cartCount || 0);
      onAddToCart?.();
      if (platform === "mobile") message.success("已加入购物车");
      addToCartDataPush({ cartObj: { cartId: activityId },
        placement: platform === "pc" ? "marketing_detail" : "marketing", method: "product_card",
        products: [{ name: product.productName, catalogId: product.sku, quantity,
          price: (Number(product.finalPrice || 0) * quantity).toFixed(2) }],
      });
    } catch (error) {
      console.error("Add-on purchase add to cart failed", error);
      message.error("加入购物车失败, 可能是该产品不支持在线下单，如果问题持续，请联系客服");
    } finally {
      pending.current = false;
      setProcessing(false);
    }
  };
  return { processing, addToCart };
}
